import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { ObraCoverImage } from '@/components/obra/ObraCoverImage';
import { getObraImageUrl } from '@/utils/image-utils';
import { generateChapterLink, generateWorkLink } from '@/utils/utils';

export type ColecaoCardContext = 'continue' | 'favorite' | 'status';

export interface ColecaoObraCardWork {
    id: number;
    nome: string;
    imagem?: string;
    formato?: { id: number; nome: string };
    total_capitulos?: number;
    total_lidos?: number;
    ultimo_lido?: { cap_num?: number; numero?: number; cap_id?: number } | null;
    proximo_lido?: { cap_id?: number; cap_num?: number; numero?: number } | null;
    /** Só favoritos: último capítulo lançado na obra. */
    ultimo_publicado?: { cap_id?: number; cap_num?: number; cap_nome?: string } | null;
}

interface ColecaoObraCardProps {
    work: ColecaoObraCardWork;
    /** Define cores, rodapé e quais campos aparecem (cada aba da coleção). */
    context: ColecaoCardContext;
    showFooter?: boolean;
    /** Esconde «Continuar · cap.» e «Abrir obra» (ex.: coleção de outro utilizador). */
    hideFootActionButtons?: boolean;
    overlayStart?: ReactNode;
    overlayEnd?: ReactNode;
    /** Cartão com contorno único alinhado à home (Continue / Atualizados). */
    homeListCard?: boolean;
}

/** Mesma linguagem visual que ObraAtualizadaCard / Select: bordas white/10, fundos white/[0.03], acento só onde importa. */
const ACCENT: Record<
    ColecaoCardContext,
    {
        coverTint: string;
        shadow: string;
        fill: string;
        pubLink: string;
        readMuted: string;
        readLink: string;
        rowHover: string;
        btnBorder: string;
        btnIcon: string;
    }
> = {
    continue: {
        coverTint: 'bg-white/[0.03]',
        shadow: '#f97316',
        fill: 'bg-orange-500',
        pubLink: 'text-white/85 group-hover/row:text-white',
        readMuted: 'text-white/45',
        readLink: 'text-orange-400/90 group-hover/row:text-orange-300',
        rowHover: 'hover:bg-white/[0.06] hover:border-white/15',
        btnBorder: 'border-white/10 hover:border-orange-500/30 hover:bg-white/[0.04]',
        btnIcon: 'text-orange-400/80',
    },
    favorite: {
        coverTint: 'bg-yellow-500/[0.04]',
        shadow: '#eab308',
        fill: 'bg-yellow-500',
        pubLink: 'text-white/85 group-hover/row:text-white',
        readMuted: 'text-white/45',
        readLink: 'text-yellow-400/85 group-hover/row:text-yellow-300',
        rowHover: 'hover:bg-white/[0.06] hover:border-white/15',
        btnBorder: 'border-white/10 hover:border-yellow-500/25 hover:bg-white/[0.04]',
        btnIcon: 'text-yellow-400/80',
    },
    status: {
        coverTint: 'bg-orange-500/[0.04]',
        shadow: '#f97316',
        fill: 'bg-orange-500',
        pubLink: 'text-white/85 group-hover/row:text-white',
        readMuted: 'text-white/45',
        readLink: 'text-orange-300/90 group-hover/row:text-orange-200',
        rowHover: 'hover:bg-white/[0.06] hover:border-white/15',
        btnBorder: 'border-white/10 hover:border-orange-500/30 hover:bg-white/[0.04]',
        btnIcon: 'text-orange-300/80',
    },
};

type ColecaoAccent = (typeof ACCENT)[ColecaoCardContext];

function FooterFavorite({ work, a }: { work: ColecaoObraCardWork; a: (typeof ACCENT)['favorite'] }) {
    const pub = work.ultimo_publicado;
    const lid = work.ultimo_lido;
    const pubNum = pub?.cap_num;
    const lidNum = lid?.cap_num ?? lid?.numero;
    const rowClass =
        'group/row flex items-center justify-between gap-2 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2.5 text-left text-sm transition-colors duration-300 md:py-2';

    return (
        <div className="mt-auto flex flex-col gap-1.5">
            {pubNum != null ? (
                pub?.cap_id ? (
                    <Link
                        to={generateChapterLink(pub.cap_id)}
                        onClick={(e) => e.stopPropagation()}
                        className={`${rowClass} ${a.rowHover}`}
                    >
                        <span className={`min-w-0 truncate font-medium ${a.pubLink}`}>
                            <span className="text-[11px] text-white/40 font-medium uppercase tracking-wide md:text-[10px]">Publicado</span>
                            <span className="block truncate text-[15px] md:text-[13px]">
                                Cap. {pubNum}
                                {pub.cap_nome && pub.cap_nome !== String(pubNum) ? ` · ${pub.cap_nome}` : ''}
                            </span>
                        </span>
                    </Link>
                ) : (
                    <div className={`${rowClass} pointer-events-none`}>
                        <span className={`min-w-0 truncate font-medium ${a.pubLink}`}>
                            <span className="text-[11px] text-white/40 font-medium uppercase tracking-wide md:text-[10px]">Publicado</span>
                            <span className="block truncate text-[15px] md:text-[13px]">Cap. {pubNum}</span>
                        </span>
                    </div>
                )
            ) : (
                <div className={`${rowClass} pointer-events-none opacity-60`}>
                    <span className="text-xs text-white/40">Sem capítulo publicado</span>
                </div>
            )}
            {lidNum != null && lidNum > 0 ? (
                lid?.cap_id ? (
                    <Link
                        to={generateChapterLink(lid.cap_id)}
                        onClick={(e) => e.stopPropagation()}
                        className={`${rowClass} ${a.rowHover}`}
                    >
                        <span className="min-w-0 truncate font-medium">
                            <span className={`text-[11px] font-medium uppercase tracking-wide md:text-[10px] ${a.readMuted}`}>Leitura</span>
                            <span className={`block truncate text-[15px] md:text-[13px] ${a.readLink}`}>Até o cap. {lidNum}</span>
                        </span>
                    </Link>
                ) : (
                    <div className={`${rowClass} pointer-events-none`}>
                        <span className="min-w-0 truncate font-medium">
                            <span className={`text-[11px] font-medium uppercase tracking-wide md:text-[10px] ${a.readMuted}`}>Leitura</span>
                            <span className={`block truncate text-[15px] md:text-[13px] ${a.readLink}`}>Até o cap. {lidNum}</span>
                        </span>
                    </div>
                )
            ) : (
                <div className={`${rowClass} pointer-events-none`}>
                    <span className={`text-xs ${a.readMuted}`}>Leitura ainda não iniciada</span>
                </div>
            )}
        </div>
    );
}

function FooterContinueStatus({
    work,
    context,
    a,
    hideFootActionButtons,
}: {
    work: ColecaoObraCardWork;
    context: 'continue' | 'status';
    a: ColecaoAccent;
    hideFootActionButtons?: boolean;
}) {
    const readNum = work.total_lidos ?? work.ultimo_lido?.cap_num ?? work.ultimo_lido?.numero ?? 0;
    const nextCap = work.proximo_lido;
    const nextNum = nextCap?.cap_num ?? nextCap?.numero;
    const total = Number(work.total_capitulos) || 0;
    const denom = total > 0 ? total : 1;
    const pct = Math.min(100, Math.round((readNum / denom) * 100));
    const hasNext = nextCap?.cap_id != null && nextNum != null;
    const isCaughtUp = total > 0 && readNum >= total;

    const actionClass = `flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors duration-300 md:gap-1.5 md:py-2 md:text-xs ${a.btnBorder} text-white/75 hover:text-white`;

    return (
        <div className="mt-auto flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2 text-xs font-medium text-white/45 md:text-[10px]">
                <span className="min-w-0 truncate">
                    {readNum > 0 ? `Cap. ${readNum} lido` : 'Nenhum capítulo lido'}
                    {total > 0 ? ` · ${total} no total` : ''}
                </span>
                {total > 0 ? <span className="shrink-0 tabular-nums text-white/55">{pct}%</span> : null}
            </div>
            {total > 0 ? (
                <div className="h-0.5 w-full overflow-hidden rounded-full bg-white/[0.08]">
                    <div className={`h-full rounded-full transition-all duration-500 ${a.fill}`} style={{ width: `${pct}%` }} />
                </div>
            ) : null}
            {!hideFootActionButtons ? (
                hasNext ? (
                    <Link to={generateChapterLink(nextCap.cap_id!)} onClick={(e) => e.stopPropagation()} className={`group/cap ${actionClass}`}>
                        <Play className={`h-4 w-4 shrink-0 md:h-3 md:w-3 ${a.btnIcon}`} />
                        <span className="md:hidden">cap. {nextNum}</span>
                        <span className="hidden md:inline">Continuar · cap. {nextNum}</span>
                    </Link>
                ) : isCaughtUp && readNum > 0 ? (
                    <p className="text-center text-xs font-medium text-white/40 md:text-[10px]">Em dia com os lançamentos</p>
                ) : readNum > 0 && total > 0 ? (
                    <Link to={generateWorkLink(work.id)} onClick={(e) => e.stopPropagation()} className={`group/cap ${actionClass}`}>
                        <Play className={`h-4 w-4 shrink-0 md:h-3 md:w-3 ${a.btnIcon}`} />
                        Abrir obra
                    </Link>
                ) : null
            ) : null}
        </div>
    );
}

export default function ColecaoObraCard({
    work,
    context,
    showFooter = true,
    hideFootActionButtons = false,
    overlayStart,
    overlayEnd,
    homeListCard = false,
}: ColecaoObraCardProps) {
    const a = ACCENT[context];
    const shadowAlpha = context === 'favorite' ? '18' : context === 'status' ? '16' : '10';
    const shadowHover = context === 'favorite' ? '28' : context === 'status' ? '24' : '20';

    const showFooterFavorite = showFooter && context === 'favorite';
    const showFooterReading = showFooter && (context === 'continue' || context === 'status');
    const readNum = work.total_lidos ?? work.ultimo_lido?.cap_num ?? work.ultimo_lido?.numero ?? 0;
    const hasReadingFooter =
        showFooterReading &&
        (Number(work.total_capitulos) > 0 || readNum > 0 || work.proximo_lido?.cap_id != null);

    const rootClass = homeListCard
        ? 'group flex h-full flex-col gap-2 rounded-xl border border-white/10 bg-neutral-900/40 p-2.5 shadow-sm transition-all duration-500 hover:border-white/20'
        : 'group flex h-full flex-col gap-2 transition-all duration-500';

    const coverClass = homeListCard
        ? 'relative block aspect-[3/4] w-full flex-shrink-0 overflow-hidden rounded-lg border border-white/5 bg-black/20 backdrop-blur-md transition-all duration-500 group-hover:scale-[1.02] group-hover:border-white/15'
        : `relative block aspect-[3/4] w-full flex-shrink-0 overflow-hidden rounded-xl border border-white/10 backdrop-blur-md transition-all duration-500 group-hover:scale-[1.02] group-hover:border-white/20 ${a.coverTint}`;

    return (
        <div className={rootClass}>
            <Link
                to={generateWorkLink(work.id)}
                className={coverClass}
                style={
                    homeListCard
                        ? undefined
                        : {
                              boxShadow: `0 10px 28px -12px ${a.shadow}${shadowAlpha}`,
                          }
                }
                onMouseEnter={
                    homeListCard
                        ? undefined
                        : (e) => {
                              e.currentTarget.style.boxShadow = `0 14px 36px -12px ${a.shadow}${shadowHover}`;
                          }
                }
                onMouseLeave={
                    homeListCard
                        ? undefined
                        : (e) => {
                              e.currentTarget.style.boxShadow = `0 10px 28px -12px ${a.shadow}${shadowAlpha}`;
                          }
                }
            >
                <ObraCoverImage
                    src={getObraImageUrl(work.id, work.imagem, 600) || work.imagem || '/placeholder-book.jpg'}
                    alt={work.nome}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-50 transition-opacity duration-500 group-hover:opacity-40" />
                {overlayStart ? <div className="pointer-events-auto absolute left-2 top-2 z-20">{overlayStart}</div> : null}
                {overlayEnd ? <div className="pointer-events-none absolute right-2 top-2 z-10">{overlayEnd}</div> : null}
            </Link>

            <div className="flex min-h-0 flex-1 flex-col gap-1.5">
                <Link to={generateWorkLink(work.id)} className="block h-[2.5rem] shrink-0 overflow-hidden">
                    <h3
                        className="line-clamp-2 text-sm font-medium leading-tight text-white transition-colors group-hover:text-white"
                        title={work.nome}
                    >
                        {work.nome}
                    </h3>
                </Link>
                {showFooterFavorite ? <FooterFavorite work={work} a={a} /> : null}
                {hasReadingFooter ? (
                    <FooterContinueStatus
                        work={work}
                        context={context as 'continue' | 'status'}
                        a={a}
                        hideFootActionButtons={hideFootActionButtons}
                    />
                ) : null}
            </div>
        </div>
    );
}
